import React from 'react'
import { useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientMatchedWithoutActionsCount,
  selectCrossClientSelectedTab,
  selectCrossClientTabCounts,
} from '../../../store/crossClient/crossClientResults'
import { CrossClientResultTabs } from '../../../models/CrossClient'
import { CrossClientMatchActions } from './CrossClientMatchActions'
import { pluralize } from '../utils'
import './CrossClientResultBanner.scss'

interface BannerShellProps {
  children: React.ReactNode
  actions?: React.ReactNode
}

const BannerShell: React.FC<BannerShellProps> = ({ children, actions }) => (
  <div className='cross-client-result-banner'>
    <div className='d-flex align-items-center gap-2'>
      <i className='bi bi-info-circle zocks-text-gray-600' aria-hidden='true' />
      <p className='paragraph-small mb-0 zocks-text-gray-900'>{children}</p>
    </div>
    {actions ?? null}
  </div>
)

export const CrossClientResultBanner: React.FC = () => {
  const selectedTab = useAppSelector(selectCrossClientSelectedTab)
  const counts = useAppSelector(selectCrossClientTabCounts)
  const matchedWithoutActions = useAppSelector(selectCrossClientMatchedWithoutActionsCount)

  if (selectedTab === CrossClientResultTabs.MATCH) {
    if (matchedWithoutActions <= 0) {
      return null
    }
    return (
      <BannerShell actions={<CrossClientMatchActions />}>
        {matchedWithoutActions.toLocaleString()} matching {pluralize(matchedWithoutActions, 'client')} with no actions yet. Review them and pick an action.
      </BannerShell>
    )
  }

  if (selectedTab === CrossClientResultTabs.POTENTIAL) {
    if (counts.potential <= 0) {
      return null
    }
    return <BannerShell>These clients may qualify but couldn&apos;t be confirmed yet. Review them manually or run a deeper scan to validate.</BannerShell>
  }

  if (selectedTab === CrossClientResultTabs.REMAINING) {
    if (counts.remaining <= 0) {
      return null
    }
    return <BannerShell>These clients couldn&apos;t be evaluated from Client Profiles in Zocks. Run a scan to find matches.</BannerShell>
  }

  if (selectedTab === CrossClientResultTabs.MISSING_DATA) {
    if (counts.missingData <= 0) {
      return null
    }
    return (
      <BannerShell>
        {counts.missingData.toLocaleString()} {pluralize(counts.missingData, 'client')} couldn&apos;t be evaluated. Some data was missing from their profiles.
      </BannerShell>
    )
  }

  if (counts.noMatch <= 0) {
    return null
  }

  return <BannerShell>These clients didn&apos;t qualify based on the data collected so far.</BannerShell>
}
